/* eslint-disable */
// ============================================================================
// GENERATED FILE — DO NOT EDIT.
// Source of truth: functions/src/domain/onboardingPermissionsCore.js
// Regenerate with:  npm run sync:onboarding-perms
// CI enforces sync: npm run sync:onboarding-perms:check
// ============================================================================
/**
 * onboardingPermissionsCore.js — RW-733: THE single source of truth for the
 * onboarding checklist toggle-permission model.
 *
 * AUTHORED HERE: functions/src/domain/onboardingPermissionsCore.js
 * GENERATED COPY: src/utils/onboardingPermissionsCore.js
 *   (regenerate with `npm run sync:onboarding-perms`; CI fails on drift via
 *   `npm run sync:onboarding-perms:check`)
 *
 * Why a generated copy instead of one physical file: CRA's ModuleScopePlugin
 * blocks client imports from outside src/, and `firebase deploy` uploads only
 * the functions/ tree — so each build needs the file inside its own tree. The
 * sync check turns what used to be two hand-maintained implementations (the
 * RW-698/RW-725/RW-731 drift class) into one authored file + one byte-checked
 * artifact.
 *
 * Framework-neutral CJS: no React, no firebase, no import.meta. The role
 * helpers are INJECTED via createOnboardingPermissions() because the roles
 * module is itself a client/server twin pair (src/lib/roles.js vs
 * functions/src/util/roles.js) with identical semantics.
 *
 * RW-564 — DUAL-LAYER NOTE on the string `agent_services`:
 *   Layer A: an assignment-vocabulary token on a template task
 *     ("this task is assigned to agent-services staff")
 *   Layer B: an actual platform role on a user account, added in RW-564
 *     ("this user has the agent_services role")
 *   ASSIGNEE_ROLE_MAP is the bridge — the platform role `agent_services`
 *   satisfies the vocabulary token of the same name, AND `admin`/super_admin
 *   continue to satisfy it for backward-compat with the v1 era when only
 *   admin existed.
 */

// Which platform roles satisfy each template assignee token.
// Do NOT include super_admin here — it is applied as a global override.
//
// RW-698: office_coordinator was in the client map but missing from the server
// twin (items denied server-side while the UI offered them). RW-725: same
// class for marketing / relo_services. This single map ends that drift class.
const ASSIGNEE_ROLE_MAP = {
  agent_services: ['agent_services', 'admin'],
  tc: ['tc', 'admin'],
  trainer: ['trainer', 'admin'],
  broker: ['broker', 'admin'],
  office_coordinator: ['office_coordinator', 'admin'],
  marketing: ['marketing', 'admin'],
  relo_services: ['relo_services', 'admin'],
  admin: ['admin'],
  super_admin: [], // super_admin handled by the global override
};

// RW-735: ordered set of assignee tokens offered in every onboarding task
// assignee picker, checklist filter, and progress breakdown. super_admin is
// excluded — work is never queued to super_admin. `agent` resolves via
// instance.agentUid, not the role map.
const ASSIGNABLE_TOKENS = [
  'agent', 'agent_services', 'tc', 'trainer', 'broker',
  'office_coordinator', 'marketing', 'relo_services', 'admin',
];

/**
 * Normalize an item's assignees to a string[] array.
 * Handles:
 *   - new schema: item.assignTo: string[]
 *   - legacy schema: item.assignedTo: "staff" | "agent"
 *   - missing/malformed → ['agent_services'] (staff default)
 */
function getItemAssignees(item) {
  if (!item) return ['agent_services'];
  if (Array.isArray(item.assignTo) && item.assignTo.length > 0) {
    return item.assignTo;
  }
  // Legacy fallback. 'staff' historically meant anyone on staff could toggle
  // — map to 'agent_services' (the admin-equivalent role).
  const legacy = item.assignedTo;
  if (legacy === 'agent') return ['agent'];
  if (legacy === 'staff') return ['agent_services'];
  return ['agent_services'];
}

/**
 * RW-735: Is this item the agent's own to do? True if 'agent' is among its
 * assignees. Works on both the new assignTo[] schema and legacy assignedTo.
 */
function isAgentItem(item) {
  return getItemAssignees(item).includes('agent');
}

/**
 * RW-735: Is this item handled by staff (not the agent)? Agent ownership is
 * checked FIRST, before the legacy `staffOnly`/`assignedTo:'staff'` flags —
 * reassigning a task to the agent updates `assignTo` but historically left the
 * legacy flags stale. The new schema is authoritative; legacy flags are only a
 * fallback for items that predate it.
 */
function isStaffItem(item) {
  if (isAgentItem(item)) return false;
  if (item && (item.assignedTo === 'staff' || item.staffOnly)) return true;
  return !isAgentItem(item);
}

/**
 * Bind the permission checks to a roles module. Both twins expose the same
 * helpers; each side passes its own:
 *   client: src/lib/roles.js          (ESM)
 *   server: functions/src/util/roles.js (CJS)
 *
 * @param {object} rolesApi — { PERMANENT_SUPER_ADMIN_EMAIL, ACCESS_LEVELS,
 *   normalizeRoles, hasAnyRole, accessLevelForRoles, isElevated }
 * @returns {{ resolveActor, canToggleItem, userMatchesItem }}
 */
function createOnboardingPermissions(rolesApi) {
  const {
    PERMANENT_SUPER_ADMIN_EMAIL,
    ACCESS_LEVELS,
    normalizeRoles,
    hasAnyRole,
    accessLevelForRoles,
    isElevated,
  } = rolesApi;

  /**
   * Normalize any actor shape to { uid, email, roles[], accessLevel }.
   * Tolerates the client user doc shape ({ roles[], accessLevel } or legacy
   * { role }) and the server's pre-resolved caller — resolution is idempotent.
   */
  function resolveActor(actor) {
    const roles = normalizeRoles(actor && actor.roles != null ? actor.roles : actor && actor.role);
    const accessLevel = (actor && actor.accessLevel) || accessLevelForRoles(roles);
    const email = ((actor && actor.email) || '').toLowerCase();
    return { uid: actor ? actor.uid : undefined, email, roles, accessLevel };
  }

  /**
   * Can `actor` toggle `item` on `instance`?
   *
   * Rules (short-circuit in order):
   *   1. Permanent owner email OR super_admin accessLevel → always allow.
   *   2. If assignees include "agent" AND actor uid == instance.agentUid →
   *      allow. Keyed on uid + instance ONLY, never on a role claim, so an
   *      agent is never locked out of their own items even if their roles are
   *      missing/stripped (the RW-731 Bug B lesson — the server always worked
   *      this way; the client twin used to bail early on roleless users).
   *   3. Any admin-or-above (isElevated) can toggle any non-agent item.
   *   4. If the actor's granular roles[] intersect a token's allowed roles → allow.
   *   5. Otherwise no.
   */
  function canToggleItem(actor, item, instance) {
    if (!actor) return false;
    const caller = resolveActor(actor);

    if (caller.email && caller.email === PERMANENT_SUPER_ADMIN_EMAIL) return true;
    if (caller.accessLevel === ACCESS_LEVELS.SUPER_ADMIN) return true;

    const assignees = getItemAssignees(item);
    for (const token of assignees) {
      if (token === 'agent') {
        if (instance && instance.agentUid === caller.uid) return true;
        continue;
      }
      if (isElevated(caller.accessLevel)) return true;
      const allowed = ASSIGNEE_ROLE_MAP[token] || [];
      if (hasAnyRole(caller.roles, allowed)) return true;
    }
    return false;
  }

  /**
   * Does `actor` match any of the item's assignees? (Filter-only, not
   * permission.) Identical to canToggleItem but WITHOUT the super_admin /
   * owner-email overrides — used by the "My Items" filter so super_admins
   * don't match every item.
   */
  function userMatchesItem(actor, item, instance) {
    if (!actor) return false;
    const caller = resolveActor(actor);
    const assignees = getItemAssignees(item);
    for (const token of assignees) {
      if (token === 'agent') {
        if (instance && instance.agentUid === caller.uid) return true;
        continue;
      }
      // Personal filter: NO global admin override here (an admin shouldn't
      // match every item in "My Items"). Only an actual role intersection.
      if (token === 'super_admin' && caller.roles.includes('super_admin')) return true;
      const allowed = ASSIGNEE_ROLE_MAP[token] || [];
      if (hasAnyRole(caller.roles, allowed)) return true;
    }
    return false;
  }

  return { resolveActor, canToggleItem, userMatchesItem };
}

module.exports = {
  ASSIGNEE_ROLE_MAP,
  ASSIGNABLE_TOKENS,
  getItemAssignees,
  isAgentItem,
  isStaffItem,
  createOnboardingPermissions,
};
